import React from 'react';
import styled from 'styled-components';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Code2,
  Lightbulb,
  LogOut,
  Plus,
  RotateCcw,
  Search,
  Zap,
} from 'lucide-react';

const Panel = styled.aside`
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.07);
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  max-height: calc(100vh - 120px);
  overflow: auto;
  padding: 16px;

  @media (max-width: 1024px) {
    max-height: none;
    order: 2;
  }

  @media (max-width: 640px) {
    border-radius: 12px 12px 0 0;
    bottom: 0;
    left: 0;
    position: sticky;
    right: 0;
    z-index: 20;
  }
`;

const LessonKicker = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const LessonTitle = styled.h2`
  color: #0f172a;
  font-size: 1.15rem;
  line-height: 1.25;
  margin: 2px 0 0;
`;

const StepMeta = styled.div`
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  justify-content: space-between;
`;

const ProgressTrack = styled.div`
  background: #e2e8f0;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  background: linear-gradient(90deg, #2c9fa4 0%, #2563eb 100%);
  height: 100%;
  transition: width 0.2s ease;
  width: ${props => props.$percent}%;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StepTitle = styled.h3`
  color: #111827;
  font-size: 1rem;
  line-height: 1.28;
  margin: 0;
`;

const Instruction = styled.p`
  color: #1f2937;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 0;
`;

const Explanation = styled.p`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-size: 0.86rem;
  line-height: 1.5;
  margin: 0;
  padding: 10px 12px;
`;

const Feedback = styled.div`
  align-items: flex-start;
  background: ${props => props.$valid ? '#f0fdf4' : '#fffbeb'};
  border: 1px solid ${props => props.$valid ? '#bbf7d0' : '#fde68a'};
  border-radius: 8px;
  color: ${props => props.$valid ? '#166534' : '#92400e'};
  display: flex;
  font-size: 0.86rem;
  gap: 8px;
  line-height: 1.45;
  padding: 10px 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PanelButton = styled.button`
  align-items: center;
  background: ${props => props.$primary ? '#2c9fa4' : '#f8fafc'};
  border: 1px solid ${props => props.$primary ? '#2c9fa4' : '#cbd5e1'};
  border-radius: 8px;
  color: ${props => props.$primary ? '#ffffff' : '#334155'};
  cursor: pointer;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 850;
  gap: 7px;
  justify-content: center;
  min-height: 38px;
  padding: 8px 11px;

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? '#1f7f84' : '#eef2f7'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.52;
  }

  &:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.26);
    outline-offset: 2px;
  }
`;

const PartGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const PartButton = styled(PanelButton)`
  justify-content: flex-start;
  width: 100%;
`;

const HintList = styled.div`
  display: grid;
  gap: 8px;
`;

const Hint = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  color: #1e40af;
  font-size: 0.84rem;
  line-height: 1.45;
  padding: 9px 10px;
`;

const Completion = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #166534;
  display: grid;
  gap: 6px;
  padding: 12px;
`;

const CompletionTitle = styled.div`
  color: #14532d;
  font-size: 0.95rem;
  font-weight: 900;
`;

const Section = styled.div`
  display: grid;
  gap: 9px;
`;

const getPartLabel = (componentKey) => {
  switch (componentKey) {
    case 'dc_power':
      return '5 V Source';
    case 'led':
      return 'LED';
    case 'resistor':
      return 'Resistor';
    case 'breadboard':
      return 'Breadboard';
    default:
      return componentKey;
  }
};

const BuilderTutorialPanel = ({
  lesson,
  step,
  stepIndex,
  validationResult,
  hintCount,
  onShowHint,
  onPrevious,
  onNext,
  onRestart,
  onExit,
  onAddComponent,
  onToggleCodePanel,
  onToggleTrace,
  partAvailability = {},
  canShowCodePanel = false,
  codePanelOpen = false,
  traceEnabled,
  isCompleted,
}) => {
  if (!lesson || !step) {
    return null;
  }

  const stepCount = lesson.steps.length;
  const progressPercent = Math.round(((stepIndex + (validationResult?.valid ? 1 : 0)) / stepCount) * 100);
  const hints = Array.isArray(step.hints) ? step.hints : [];
  const visibleHints = hints.slice(0, hintCount);
  const stepParts = Array.isArray(step.availableComponents) ? step.availableComponents : [];
  const canShowMoreHints = hintCount < hints.length;
  const feedbackMessage = validationResult?.message || '';

  return (
    <Panel aria-label={`${lesson.title} tutorial panel`}>
      <Section>
        <LessonKicker>Guided Tutorial</LessonKicker>
        <LessonTitle>{lesson.title}</LessonTitle>
      </Section>

      <Section aria-label="Lesson progress">
        <StepMeta>
          <span>Step {stepIndex + 1} of {stepCount}</span>
          <span>{progressPercent}%</span>
        </StepMeta>
        <ProgressTrack aria-hidden="true">
          <ProgressFill $percent={progressPercent} />
        </ProgressTrack>
      </Section>

      {isCompleted ? (
        <Completion role="status">
          <CompletionTitle>{lesson.completion?.title || 'Lesson complete'}</CompletionTitle>
          <div>{lesson.completion?.message || 'Nice work. You completed the lesson.'}</div>
        </Completion>
      ) : (
        <>
          <Section>
            <StepTitle>{step.title}</StepTitle>
            <Instruction>{step.instruction}</Instruction>
            {step.explanation ? <Explanation>{step.explanation}</Explanation> : null}
          </Section>

          {stepParts.length > 0 ? (
            <Section aria-label="Lesson parts">
              <LessonKicker>Lesson Parts</LessonKicker>
              <PartGrid>
                {stepParts.map((componentKey) => {
                  const partStatus = partAvailability[componentKey] || {};
                  const isPartDisabled = Boolean(partStatus.disabled);
                  const partLabel = getPartLabel(componentKey);

                  return (
                    <PartButton
                      key={componentKey}
                      type="button"
                      disabled={isPartDisabled}
                      title={
                        isPartDisabled
                          ? (partStatus.message || `${partLabel} already added`)
                          : `Add ${partLabel}`
                      }
                      onClick={() => onAddComponent(componentKey)}
                    >
                      {isPartDisabled ? <CheckCircle2 size={15} /> : <Plus size={15} />}
                      {isPartDisabled ? `${partLabel} Added` : partLabel}
                    </PartButton>
                  );
                })}
              </PartGrid>
            </Section>
          ) : null}

          {step.allowedActions?.traceConnections ? (
            <PanelButton
              type="button"
              $primary={traceEnabled}
              aria-pressed={traceEnabled}
              onClick={onToggleTrace}
            >
              <Search size={15} />
              {traceEnabled ? 'Tracing On' : 'Trace Connections'}
            </PanelButton>
          ) : null}

          {canShowCodePanel ? (
            <PanelButton
              type="button"
              $primary={codePanelOpen}
              aria-pressed={codePanelOpen}
              onClick={onToggleCodePanel}
            >
              <Code2 size={15} />
              {codePanelOpen ? 'Hide Code' : 'View Code'}
            </PanelButton>
          ) : null}

          {feedbackMessage ? (
            <Feedback
              $valid={Boolean(validationResult?.valid)}
              role="status"
              aria-live="polite"
            >
              {validationResult?.valid ? <CheckCircle2 size={16} /> : <Zap size={16} />}
              <span>{feedbackMessage}</span>
            </Feedback>
          ) : (
            <div aria-live="polite" className="sr-only" />
          )}

          {visibleHints.length > 0 ? (
            <HintList>
              {visibleHints.map((hint, index) => (
                <Hint key={`${step.id}-hint-${index}`}>
                  {hint.text}
                </Hint>
              ))}
            </HintList>
          ) : null}

          <ButtonRow>
            <PanelButton
              type="button"
              onClick={onShowHint}
              disabled={!canShowMoreHints}
            >
              <Lightbulb size={15} />
              Hint
            </PanelButton>
            <PanelButton
              type="button"
              onClick={onPrevious}
              disabled={stepIndex === 0}
            >
              <ChevronLeft size={15} />
              Previous
            </PanelButton>
            <PanelButton
              type="button"
              $primary
              onClick={onNext}
              disabled={!validationResult?.valid}
            >
              Next
              <ChevronRight size={15} />
            </PanelButton>
          </ButtonRow>
        </>
      )}

      <ButtonRow>
        <PanelButton type="button" onClick={onRestart}>
          <RotateCcw size={15} />
          Restart
        </PanelButton>
        <PanelButton type="button" onClick={onExit}>
          <LogOut size={15} />
          Exit Tutorial
        </PanelButton>
      </ButtonRow>
    </Panel>
  );
};

export default BuilderTutorialPanel;

